// AdjustStockModal — one-shot manual stock correction (POST /api/adjustments/
// manual-adjust/): reason-coded, applied immediately for authorised operators,
// recorded in the movement ledger + audit log. Extracted verbatim from
// ProductDetail.tsx so the global quick-actions launcher can reuse it; the
// product page renders it unchanged.
import { useEffect, useState } from 'react';
import { Icon } from '@/foundation/icons';
import { Button, Field, Input, Modal, Select } from '@/foundation/primitives';
import { useStore } from '@/store/store';
import { infoBox } from './components';
import { useManualAdjust } from './api';
import { useOpenOwings } from './useOpenOwings';

// Reason codes for a manual stock correction (expiry disposals use the governed approval flow).
export const ADJUST_REASONS = [
  { value: 'stocktake_variance', label: 'Count correction (stocktake variance)' },
  { value: 'damage', label: 'Damage' },
  { value: 'theft', label: 'Theft / loss' },
  { value: 'recall', label: 'Recall' },
  { value: 'opened_pack_correction', label: 'Opened-pack correction' },
  { value: 'return_to_supplier', label: 'Return to supplier' },
];

export interface AdjustBatch {
  id: string;
  batchNo?: string;
  qty: number;
  alloc?: number;
  exp?: string;
  bin?: string;
  /** True when the expiry is a system default (no real expiry captured at goods-in). */
  expiryIsDefault?: boolean;
  /** Staff-flagged as excess / not-required (offered to other stores in the group). */
  isExcess?: boolean;
  excessNote?: string;
}

export function AdjustStockModal({
  batches,
  initialBatchId,
  storeId,
  productName,
  product,
  onClose,
}: {
  batches: AdjustBatch[];
  initialBatchId: string;
  storeId: string;
  productName: string;
  /** Optional product identity — enables the "N patients owing" notice. */
  product?: { id?: string; sku?: string };
  onClose: () => void;
}) {
  const { toast } = useStore();
  const adjust = useManualAdjust();
  const owings = useOpenOwings(product, storeId);
  const [batchId, setBatchId] = useState(initialBatchId);
  const batch = batches.find((b) => b.id === batchId) ?? batches[0];
  const [newQty, setNewQty] = useState<number>(batch?.qty ?? 0);
  const [reason, setReason] = useState(ADJUST_REASONS[0].value);
  const [note, setNote] = useState('');

  // Re-seed the quantity field whenever the selected batch changes.
  useEffect(() => {
    setNewQty(batch?.qty ?? 0);
  }, [batchId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!batch) return null;
  const allocated = batch.alloc ?? 0;
  const delta = newQty - batch.qty;
  const belowAllocated = newQty < allocated;
  const canSave = delta !== 0 && newQty >= 0 && !belowAllocated && !adjust.isPending;

  const submit = async () => {
    try {
      const res = await adjust.mutateAsync({
        store_id: storeId,
        batch_stock_id: batch.id,
        new_qty: newQty,
        reason_code: reason,
        note: note.trim(),
      });
      toast(`Stock adjusted — on hand is now ${res.new_on_hand}`, 'success');
      onClose();
    } catch (err) {
      const body = (err as { body?: { detail?: string } }).body;
      toast(body?.detail || 'Could not adjust stock', 'error');
    }
  };

  return (
    <Modal open onClose={onClose} title="Adjust stock" subtitle={productName} width={440}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {owings.count > 0 && (
          <div style={{ ...infoBox, background: 'var(--warning-soft)' }}>
            <Icon name="clock" size={18} color="var(--warning-dark)" />
            <span>
              <b>
                {owings.count} patient{owings.count > 1 ? 's are' : ' is'} owing this product
              </b>{' '}
              ({owings.pendingUnits} unit{owings.pendingUnits > 1 ? 's' : ''} pending) — adding
              stock marks their backorder fulfillable.
            </span>
          </div>
        )}
        {batches.length > 1 && (
          <Field label="Batch">
            <Select
              value={batchId}
              onChange={(e) => setBatchId(e.target.value)}
              options={batches.map((b) => ({
                value: b.id,
                label: `${b.batchNo || b.id}${b.bin ? ` · ${b.bin}` : ''} — ${b.qty} on hand`,
              }))}
            />
          </Field>
        )}
        <div style={{ display: 'flex', gap: 14 }}>
          <Field label="Current on hand">
            <Input value={String(batch.qty)} disabled />
          </Field>
          <Field label="New on hand">
            <Input
              type="number"
              value={newQty}
              onChange={(e) => setNewQty(Math.max(0, Number(e.target.value)))}
            />
          </Field>
        </div>
        {delta !== 0 && !belowAllocated && (
          <div className="caption" style={{ color: 'var(--text-secondary)', marginTop: -8 }}>
            Change: {delta > 0 ? `+${delta}` : delta} unit(s)
          </div>
        )}
        {belowAllocated && (
          <div style={{ ...infoBox, background: 'var(--warning-soft)' }}>
            <Icon name="alerts" size={18} color="var(--warning-dark)" />
            <span>
              {allocated} unit(s) are allocated to prescriptions — new quantity can’t be lower.
            </span>
          </div>
        )}
        <Field label="Reason">
          <Select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            options={ADJUST_REASONS}
          />
        </Field>
        <Field label="Note (optional)">
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Reason detail / reference"
          />
        </Field>
        <div style={infoBox}>
          <Icon name="zap" size={18} color="var(--info-main)" />
          <span>Applied immediately and recorded in the movement ledger &amp; audit log.</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button icon="save" onClick={() => void submit()} disabled={!canSave}>
            {adjust.isPending ? 'Saving…' : 'Apply adjustment'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
